import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Modal } from '@calendly/ui/components/modal';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common';
import { moveToCheckout } from 'features/billing/actions/create_subscription';
import { addonsForMeetingIntelligencePlan } from 'features/billing/modals/plan_selection_modal/config/addons';
import { trialTypeSelector, trialEndsInDaysSelector, isTrialActiveSelector } from 'features/billing/selectors';
import { TRACKING_EVENT_NAMES, useTrack, useTrackCheckoutFlowStarted } from 'hooks/analytics_events';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import TrialEndingImage from './images/trial_ending.svg';
import { Wrapper, ModalContent, HeadingContainer, SidebarWithImage, BulletPoint, CloseButton } from './shared';
const sizedModalContentIsTabletUpCss = "s15ez0g3";
const sizedModalContentCss = "sfcc44e";
const SizedModalContent = forwardRef((props, ref) => {
  const isTabletUp = useDeviceType('isTabletUp');
  return <ModalContent ref={ref} {...props} className={classNames(sizedModalContentCss, {
    [sizedModalContentIsTabletUpCss]: isTabletUp
  }, props.className)} />;
});
const contentContainerCss = "c2ly5r8";
const ContentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentContainerCss, props.className)} />;
});
const footerContainerCss = "f1acwwfn";
const FooterContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(footerContainerCss, props.className)} />;
});
const fullWidthButtonCss = "f1w2sw18";
const FullWidthButton = forwardRef((props, ref) => <Button ref={ref} {...props} className={classNames(fullWidthButtonCss, props.className)} />);
export const AiTrialEndingModal = () => {
  const dispatch = useDispatch();
  const {
    pathname
  } = useLocation();
  const track = useTrack();
  const trackCheckoutFlowStarted = useTrackCheckoutFlowStarted();
  const trialActive = useSelector(isTrialActiveSelector);
  const trialEndsInDays = useSelector(trialEndsInDaysSelector);
  const trialType = useSelector(trialTypeSelector);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const [isVisible, setIsVisible] = useState(false);
  const {
    data: {
      id: userId,
      activation_data: {
        has_seen_ai_trial_ending_modal: hasSeenAiTrialEndingModal
      }
    }
  } = useGetUserQuery();
  const [update] = useUpdateUserActivationDataMutation();
  const daysSinceTrialStart = 21 - trialEndsInDays;
  const modalName = `ai_notetaker_trial_expiring_${trialEndsInDays}_${trialEndsInDays === 1 ? 'day' : 'days'}`;
  const isIneligible = !trialActive || trialType !== 'calendly_all' || trialEndsInDays > 3 || trialEndsInDays <= 0 || hasSeenAiTrialEndingModal;
  const closeModal = () => {
    update({
      has_seen_ai_trial_ending_modal: true
    });
    setIsVisible(false);
  };
  const onClose = (isAcknowledge = false) => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        modal_name: modalName,
        action: isAcknowledge ? 'acknowledged' : 'dismissed',
        path: pathname
      }
    });
    closeModal();
  };
  const onViewAllPlans = () => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        modal_name: modalName,
        action: 'compare_plans',
        path: pathname
      }
    });
    closeModal();
    dispatch(moveToCheckout({
      isDowngrading: false
    }));
  };
  const onUpgrade = () => {
    trackCheckoutFlowStarted({
      checkoutFlowLocation: modalName,
      checkoutPlanName: 'teams_plus',
      checkoutPlanBillingTerm: 'yearly',
      daysSinceTrialStart
    });
    dispatch(moveToCheckout({
      directToTier: 'teams',
      featureKey: 'meeting_intelligence_beta',
      selectedAddons: addonsForMeetingIntelligencePlan({
        tier: 'teams',
        billingPeriod: 'year'
      }),
      analyticCheckoutFlowLocation: 'ai_trial_ending_modal',
      skipPreface: true
    }));
    closeModal();
  };
  useEffect(() => {
    if (isVisible) {
      track({
        name: TRACKING_EVENT_NAMES.PAYWALL_SHOWN,
        entities: ['user', 'organization', 'attribution'],
        properties: {
          paywall_feature: modalName,
          paywall_type: 'popup',
          plan_required: 'teams_plus',
          days_since_trial_start: daysSinceTrialStart
        }
      });
    }
  }, [isVisible, modalName, daysSinceTrialStart, track]);
  useEffect(() => {
    if (!isVisible && !!userId && !isIneligible) {
      setIsVisible(true);
    }
  }, [isVisible, userId, isIneligible, setIsVisible]);
  if (isIneligible) return null;
  return <Modal isOpen={isVisible} onClose={() => {
    onClose(false);
  }}>
      <Wrapper>
        <SizedModalContent>
          <CloseButton onClick={() => {
          onClose(false);
        }} />
          <SidebarWithImage src={TrialEndingImage} />
          <ContentContainer>
            <div>
              <HeadingContainer>
                <HeadingText level={2}>
                  {I18n.t('publisher.meeting_intelligence.trial.trial_ending_title', {
                  count: trialEndsInDays
                })}
                </HeadingText>
              </HeadingContainer>
              <BodyText>
                {I18n.t('publisher.meeting_intelligence.trial.trial_ending_body')}
              </BodyText>
              <div>
                <BulletPoint>
                  {HtmlParser(I18n.t('publisher.meeting_intelligence.trial.bullet_one'))}
                </BulletPoint>
                <BulletPoint>
                  {HtmlParser(I18n.t('publisher.meeting_intelligence.trial.bullet_two'))}
                </BulletPoint>
                <BulletPoint>
                  {HtmlParser(I18n.t('publisher.meeting_intelligence.trial.bullet_three'))}
                </BulletPoint>
              </div>
            </div>
            <FooterContainer>
              {isAdminOrOwner ? <>
                  <Button decoration="secondary-outline" onClick={onViewAllPlans}>
                    {I18n.t('publisher.meeting_intelligence.trial.view_all_plans')}
                  </Button>
                  <Button decoration="meeting-intelligence" onClick={onUpgrade}>
                    {I18n.t('publisher.meeting_intelligence.trial.upgrade')}
                  </Button>
                </> : <FullWidthButton decoration="meeting-intelligence" onClick={() => {
              onClose(true);
            }}>
                  {I18n.t('publisher.meeting_intelligence.trial.got_it')}
                </FullWidthButton>}
            </FooterContainer>
          </ContentContainer>
        </SizedModalContent>
      </Wrapper>
    </Modal>;
};

require("./trial_ending_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./trial_ending_modal.js");